import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  TrendingUp,
  DollarSign,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  ExternalLink,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Building2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminStripe = () => {
  const [loading, setLoading] = useState(true);
  const [platformStats, setPlatformStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [dateRange, setDateRange] = useState("month");

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [statsRes, txnRes, accountsRes] = await Promise.all([
        axios.get(`${API}/admin/stripe/overview?period=${dateRange}`, { headers }),
        axios.get(`${API}/admin/stripe/transactions?limit=20`, { headers }),
        axios.get(`${API}/admin/stripe/connected-accounts`, { headers })
      ]);

      setPlatformStats(statsRes.data);
      setTransactions(txnRes.data.transactions || []);
      setConnectedAccounts(accountsRes.data.accounts || []);
    } catch (error) {
      console.error("Error fetching Stripe data:", error);
      toast.error("Failed to load Stripe data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "complete":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pending</Badge>;
      case "restricted":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Restricted</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-stripe">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Stripe Connect</h1>
          <p className="text-muted-foreground">Platform payment processing and connected accounts</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Volume</p>
                  <p className="text-3xl font-serif mt-1">
                    ${platformStats?.total_volume?.toFixed(2) || "0.00"}
                  </p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-full">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Platform Fees</p>
                  <p className="text-3xl font-serif mt-1">
                    ${platformStats?.platform_fees?.toFixed(2) || "0.00"}
                  </p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-full">
                  <Percent className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Transactions</p>
                  <p className="text-3xl font-serif mt-1">
                    {platformStats?.transaction_count || 0}
                  </p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-full">
                  <TrendingUp className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Connected Accounts</p>
                  <p className="text-3xl font-serif mt-1">
                    {platformStats?.connected_accounts || 0}
                  </p>
                </div>
                <div className="p-3 bg-amber-500/20 rounded-full">
                  <Users className="w-6 h-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Commission Rates */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Commission Rates by Plan
          </CardTitle>
          <CardDescription>Platform commission taken from each transaction</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-4">
            {[
              { plan: "Starter", rate: 10, color: "blue" },
              { plan: "Pro", rate: 6, color: "purple" },
              { plan: "Studio", rate: 3, color: "amber" },
              { plan: "Legacy", rate: 3, color: "emerald" }
            ].map((item) => (
              <div key={item.plan} className={`p-4 bg-${item.color}-500/10 rounded-lg text-center`}>
                <p className="font-medium mb-1">{item.plan}</p>
                <p className="text-2xl font-bold">{item.rate}%</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="transactions" className="space-y-6">
        <TabsList>
          <TabsTrigger value="transactions">Recent Transactions</TabsTrigger>
          <TabsTrigger value="accounts">Connected Accounts</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Platform Transactions</CardTitle>
              <CardDescription>All payments processed through the platform</CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No transactions yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Photographer</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Client</th>
                        <th className="text-right p-3 text-sm font-medium text-muted-foreground">Amount</th>
                        <th className="text-right p-3 text-sm font-medium text-muted-foreground">Platform Fee</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, index) => (
                        <tr key={txn.transaction_id || index} className="border-b border-border/50 last:border-0">
                          <td className="p-3 text-sm">
                            <div>
                              <p>{txn.created_at ? new Date(txn.created_at).toLocaleDateString() : "-"}</p>
                              <p className="text-xs text-muted-foreground">
                                {txn.created_at ? new Date(txn.created_at).toLocaleTimeString() : ""}
                              </p>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="font-medium">{txn.photographer_name || txn.photographer_id}</span>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {txn.customer_email || txn.booking_info?.customer_name || "-"}
                          </td>
                          <td className="p-3 text-right font-medium">
                            ${txn.amount?.toFixed(2) || "0.00"}
                          </td>
                          <td className="p-3 text-right text-sm text-green-500">
                            +${txn.application_fee?.toFixed(2) || "0.00"}
                          </td>
                          <td className="p-3 text-center">
                            {txn.status === "completed" ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                Completed
                              </Badge>
                            ) : txn.status === "pending" ? (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                Pending
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                {txn.status}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Connected Stripe Accounts</CardTitle>
              <CardDescription>Photographers with Stripe Connect enabled</CardDescription>
            </CardHeader>
            <CardContent>
              {connectedAccounts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No connected accounts yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Photographer</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Plan</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Account ID</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-3 text-sm font-medium text-muted-foreground">Connected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connectedAccounts.map((account, index) => (
                        <tr key={account.photographer_id || index} className="border-b border-border/50 last:border-0">
                          <td className="p-3">
                            <div>
                              <p className="font-medium">{account.user?.name || "Unknown"}</p>
                              <p className="text-sm text-muted-foreground">{account.user?.email}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="capitalize">
                              {account.current_plan_key}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {account.stripe_connect_id?.substring(0, 20)}...
                            </code>
                          </td>
                          <td className="p-3 text-center">
                            {getStatusBadge(account.stripe_connect_status)}
                          </td>
                          <td className="p-3 text-right text-sm text-muted-foreground">
                            {account.stripe_connect_created 
                              ? new Date(account.stripe_connect_created).toLocaleDateString() 
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminStripe;
